/**
 * Materialklassen der Ersatzbaustoffverordnung (EBV) fuer Bodenmaterial und
 * die Bodenbewertungsstufen der einzelnen Leitstoffe.
 *
 * ═══ WARUM DIESER KATALOG IM REPO LIEGT UND NICHT IN DER DATENBANK ════════
 * Wuerde er als Stammtabelle gefuehrt und fortgeschrieben, aenderte sich
 * rueckwirkend, was in einem freigegebenen Plan als Einstufung galt. Die
 * Fassung wird deshalb am Datensatz festgehalten
 * (`asplaene.katalog_version`). Wortgleiche Begruendung wie beim
 * Fragenkatalog der Planungsphase und den Gewerkvorlagen des Handbuchs.
 *
 * ═══ DIE GRENZE DIESES KATALOGS ═══════════════════════════════════════════
 * Die EBV stuft Bodenmaterial ueber ein Zusammenspiel aus Feststoff- und
 * Eluatwerten, Bodenart und Einbauweise ein. Dieses Modul bildet das NICHT
 * nach — es waere eine Genauigkeit, die aus einer Handvoll Schwellenwerten
 * nicht kommt, und die Einstufung stammt ohnehin aus dem abfallrechtlichen
 * Gutachten.
 *
 * Was hier steht, ist deshalb bewusst schmal:
 *   • die RANGFOLGE der Klassen (fuer das Maximumprinzip),
 *   • die WENIGEN Schwellen, die im Arbeitsschutz taeglich gebraucht werden
 *     und belegbar sind,
 *   • die Legendentexte fuer Tabelle, Aushang und PDF.
 *
 * Wo keine Schwelle hinterlegt ist, schlaegt `einstufung.ts` NICHTS vor,
 * sondern meldet „aus dem Gutachten zu uebernehmen". Ein geratener Vorschlag
 * waere hier schlimmer als keiner: er saehe aus wie ein Ergebnis.
 *
 * ═══ UND DIE WICHTIGERE GRENZE ════════════════════════════════════════════
 * Die EBV-Einstufung ist ABFALLRECHT. Fuer den Arbeitsschutz ist sie nur ein
 * Anhaltspunkt — die BG BAU nennt die Uebernahme abfallrechtlicher
 * Beurteilungen („LAGA") ausdruecklich als Mangel: sie haben „keine
 * Aussagekraft bezueglich des Arbeitsschutzes", weil Mischproben hohe
 * Einzelgehalte verduennen und die Aufnahmewege der Beschaeftigten dort keine
 * Rolle spielen. Deshalb entscheidet im A+S-Plan nicht die Materialklasse
 * ueber die Schutzklasse, sondern die taetigkeitsbezogene Beurteilung
 * (`asplan_taetigkeiten`). Die Klasse liefert nur den Ausgangspunkt.
 */

/** Fassung dieses Katalogs. Wandert bei jeder Aenderung nach oben und wird
 *  am Plan festgehalten. */
export const EBV_KATALOG_VERSION = '2026-08-22';

/**
 * Eine Materialklasse fuer Bodenmaterial.
 *
 * `rang` ist der einzige Wert, auf dem gerechnet wird. Hoeher = staerker
 * belastet. Namen aendern sich, Rangfolgen nicht.
 */
export interface EbvKlasse {
    key: string;
    label: string;
    rang: number;
    /** Kurztext fuer die Legende unter der Rasterfeldtabelle. */
    legende: string;
    /** Farbe fuer Tabelle, Rasterskizze und PDF — EINE Quelle, damit
     *  Bildschirm und Druck nicht auseinanderlaufen. */
    farbe: string;
    /** Ob diese Klasse im Arbeitsschutz als „belastet" gilt und damit einen
     *  Schwarzbereich begruendet. */
    belastet: boolean;
    veraltet?: boolean;
}

/**
 * Die Klassen in aufsteigender Belastung.
 *
 * Das Sternchen ist hier KEINE eigene Klasse: „BM-0*" ist BM-0 mit
 * `vorbehalt = true` an der Probe. Es als eigenen Rang zu fuehren hiesse,
 * einen Zustand („Analytik unvollstaendig") in eine Belastungsstufe
 * umzudeuten — dann waere ein Feld mit ausstehender Analytik automatisch
 * schlechter eingestuft als ein sauber gemessenes, und das ist es nicht.
 * Es ist unsicherer, und Unsicherheit wird ueber den Vorbehalt behandelt.
 */
export const EBV_KLASSEN: EbvKlasse[] = [
    {
        key: 'BM-0',
        label: 'BM-0',
        rang: 0,
        legende: 'unbelastetes Bodenmaterial',
        farbe: '#dcfce7',
        belastet: false,
    },
    {
        key: 'BM-F0',
        label: 'BM-F0*',
        rang: 1,
        legende: 'belastetes Material ohne maßgebliche Einzelparameter-Ueberschreitung',
        farbe: '#fef9c3',
        belastet: true,
    },
    {
        key: 'BM-F1',
        label: 'BM-F1',
        rang: 2,
        legende: 'belastetes Bodenmaterial, Einbauklasse 1',
        farbe: '#fef08a',
        belastet: true,
    },
    {
        key: 'BM-F2',
        label: 'BM-F2',
        rang: 3,
        legende: 'belastetes Bodenmaterial, Einbauklasse 2',
        farbe: '#fde047',
        belastet: true,
    },
    {
        key: 'BM-F3',
        label: 'BM-F3',
        rang: 4,
        legende: 'belastetes Material / Sonderaushub — maßgebliche Schutzmaßnahmen erforderlich',
        farbe: '#fb923c',
        belastet: true,
    },
    {
        key: 'UEBER-BM-F3',
        label: '> BM-F3',
        rang: 5,
        legende: 'oberhalb BM-F3 — gesonderte Entsorgung, höchste Schutzstufe prüfen',
        farbe: '#ef4444',
        belastet: true,
    },
];

const NACH_KEY = new Map(EBV_KLASSEN.map(k => [k.key, k]));

/** Klasse zu einem Schluessel. `null` fuer unbekannte Schluessel — ein
 *  Bestandsplan darf einen Wert tragen, den dieser Katalog nicht (mehr)
 *  kennt, und soll deswegen nicht abstuerzen. */
export const ebvKlasse = (key: string | null | undefined): EbvKlasse | null =>
    (key ? NACH_KEY.get(key) ?? null : null);

/** Rang einer Klasse; `-1` fuer unbekannt/nicht eingestuft. Bewusst
 *  NEGATIV und nicht 0: „nicht eingestuft" darf im Maximum niemals ein
 *  echtes BM-0 verdraengen, aber es darf auch nicht als unbelastet gelten. */
export const ebvRang = (key: string | null | undefined): number =>
    ebvKlasse(key)?.rang ?? -1;

/**
 * Bodenbewertungsstufen je Leitstoff.
 *
 * Diese Tabellen stehen so im A+S-Plan (Kapitel „Bewertungsgrundlagen") und
 * dienen der EINORDNUNG eines Messwerts im Text — nicht der EBV-Einstufung.
 * Sie beantworten die Frage „ist dieser Wert auffaellig?", nicht „welche
 * Materialklasse ist das?".
 */
export interface BewertungsStufe {
    /** Untergrenze einschliesslich; `null` = nach unten offen. */
    ab: number | null;
    /** Obergrenze ausschliesslich; `null` = nach oben offen. */
    bis: number | null;
    text: string;
}

export interface StoffBewertung {
    stoff_key: string;
    einheit: string;
    stufen: BewertungsStufe[];
    /**
     * Der Feststoffwert, ab dem das Material im Arbeitsschutz als
     * einstufungsrelevant belastet gilt (Schwelle zu BM-F3).
     *
     * NUR gesetzt, wo er im Regelwerk benannt ist. `null` heisst: aus dem
     * Gutachten uebernehmen, nicht rechnen.
     */
    schwelle_bm_f3: number | null;
    quelle: string;
}

export const BODEN_BEWERTUNG: StoffBewertung[] = [
    {
        stoff_key: 'pak16',
        einheit: 'mg/kg TS',
        stufen: [
            { ab: null, bis: 3, text: 'meist unauffällig' },
            { ab: 3, bis: 100, text: 'belasteter Boden / Prüfbedarf' },
            { ab: 100, bis: 1000, text: 'deutliche Kontamination' },
            { ab: 1000, bis: null, text: 'Sanierungs- / Sonderabfallthema' },
        ],
        // Bewusst null: die EBV-Schwelle fuer PAK haengt an Bodenart und
        // Einbauweise. Die Einstufung kommt aus dem Gutachten.
        schwelle_bm_f3: null,
        quelle: 'A+S-Plan Bewertungsgrundlagen; EBV-Einstufung aus dem Gutachten',
    },
    {
        stoff_key: 'hg',
        einheit: 'mg/kg TS',
        stufen: [
            { ab: null, bis: 0.3, text: 'Hintergrund-/Vorsorgebereich' },
            { ab: 0.3, bis: 2, text: 'auffällig / Untersuchungsbedarf' },
            { ab: 2, bis: 10, text: 'belasteter Boden' },
            { ab: 10, bis: 50, text: 'deutliche Kontamination' },
            { ab: 50, bis: null, text: 'Sanierungs- / Sonderabfallthema' },
        ],
        schwelle_bm_f3: null,
        quelle: 'A+S-Plan Bewertungsgrundlagen',
    },
    {
        stoff_key: 'pb',
        einheit: 'mg/kg TS',
        stufen: [
            { ab: null, bis: 40, text: 'Hintergrund-/Vorsorgebereich' },
            { ab: 40, bis: 200, text: 'auffällig / Untersuchungsbedarf' },
            { ab: 200, bis: 700, text: 'belasteter Boden' },
            { ab: 700, bis: 2000, text: 'über EBV-Materialklasse BM-F3 — einstufungs-/entsorgungsrelevant' },
            { ab: 2000, bis: null, text: 'deutliche Kontamination' },
        ],
        // Der einzige Wert dieses Katalogs, der als Schwelle taugt: er wird
        // im Regelwerk als Vergleichswert fuer Blei benannt und im
        // Musterplan zur Begruendung einer Einstufung herangezogen
        // (Feld 6: 1.220 mg/kg > 700 mg/kg).
        schwelle_bm_f3: 700,
        quelle: 'EBV-Vergleichswert Blei, im A+S-Plan benannt',
    },
];

const BEWERTUNG_NACH_STOFF = new Map(BODEN_BEWERTUNG.map(b => [b.stoff_key, b]));

export const bodenBewertung = (stoff_key: string): StoffBewertung | null =>
    BEWERTUNG_NACH_STOFF.get(stoff_key) ?? null;

/**
 * Ordnet einen Messwert in die Bewertungsstufen ein.
 *
 * Gibt `null` zurueck, wenn der Stoff keine Stufen hat oder der Wert fehlt —
 * und zwar ausdruecklich statt einer Ersatzaussage wie „unauffaellig".
 */
export const bewerteMesswert = (stoff_key: string, wert: number | null): BewertungsStufe | null => {
    if (wert === null || !Number.isFinite(wert)) return null;
    const b = BEWERTUNG_NACH_STOFF.get(stoff_key);
    if (!b) return null;
    return b.stufen.find(s =>
        (s.ab === null || wert >= s.ab) && (s.bis === null || wert < s.bis)) ?? null;
};
